import { clockOffset, type ClientMessage, type ReactionId, type ServerMessage, type WireAction } from "@guandan/protocol";

/**
 * The socket to one game room.
 *
 * Identity comes from the session cookie: the Worker resolves it and injects
 * the seat before forwarding to the room, so there is nothing to send here.
 *
 * Reconnects resume from the last sequence *received*, not the last one shown.
 * Anything still waiting to be animated has already arrived, and asking for it
 * again would deliver it twice.
 */

export type SocketStatus = "connecting" | "open" | "reconnecting" | "closed" | "gone";

export interface GameSocketHandlers {
  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus?: (status: SocketStatus) => void;
}

const PING_INTERVAL = 10_000;
const MAX_BACKOFF = 15_000;
/**
 * Reconnect attempts before checking whether the room still exists.
 *
 * A failed WebSocket handshake gives the browser no status code, so a room that
 * has been archived and answers 410 is indistinguishable from a flaky network
 * — and without this the socket retried it every fifteen seconds forever while
 * the player stared at a blank table that said "reconnecting".
 */
const PROBE_AFTER_ATTEMPTS = 2;

export class GameSocket {
  #ws: WebSocket | null = null;
  #closed = false;
  #attempt = 0;
  #pingTimer: number | null = null;
  #retryTimer: number | null = null;
  #intents = 0;
  /** Rolling estimate of server time minus local time. */
  #clockOffsetMs = 0;
  /** Highest sequence received, which is where a resume must start. */
  #received = 0;

  constructor(private readonly roomId: string, private readonly handlers: GameSocketHandlers) {}

  get clockOffsetMs(): number { return this.#clockOffsetMs; }
  get isOpen(): boolean { return this.#ws?.readyState === WebSocket.OPEN; }

  /** Server time now, as best we can estimate it. */
  serverNow(): number { return Date.now() + this.#clockOffsetMs; }

  connect(): void {
    this.#closed = false;
    this.#open();
  }

  close(): void {
    this.#closed = true;
    this.#stopTimers();
    this.#ws?.close();
    this.#ws = null;
    this.handlers.onStatus?.("closed");
  }

  /** Record how far the model has got, so a reconnect resumes from there. */
  markReceived(seq: number): void {
    this.#received = Math.max(this.#received, seq);
  }

  send(message: ClientMessage): void {
    if (this.#ws?.readyState !== WebSocket.OPEN) return;
    this.#ws.send(JSON.stringify(message));
  }

  /**
   * Send an action for the current turn.
   *
   * `expectSeq` and `turnToken` are what let the server refuse an action aimed
   * at a table that has moved on; `id` makes a retry safe to replay.
   */
  sendIntent(action: WireAction, expectSeq: number, turnToken: string): string {
    const id = `${this.roomId}:${this.#intents++}:${crypto.randomUUID()}`;
    this.send({ kind: "intent", id, expectSeq, turnToken, action });
    return id;
  }

  sendHint(expectSeq: number, turnToken: string): void {
    this.send({ kind: "hint", expectSeq, turnToken });
  }

  /** Live table decoration only — never an action in the authoritative game. */
  sendReaction(reactionId: ReactionId): void {
    this.send({ kind: "reaction", reactionId });
  }

  resync(since = 0): void {
    this.send({ kind: "resync", since });
  }

  #open(): void {
    if (this.#closed) return;
    this.handlers.onStatus?.(this.#attempt === 0 ? "connecting" : "reconnecting");

    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${location.host}/api/rooms/${this.roomId}/ws?since=${this.#received}`;
    const ws = new WebSocket(url);
    this.#ws = ws;

    ws.addEventListener("open", () => {
      this.#attempt = 0;
      this.handlers.onStatus?.("open");
      this.#ping();
      this.#pingTimer = window.setInterval(() => this.#ping(), PING_INTERVAL);
    });

    ws.addEventListener("message", (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      if (message.kind === "pong") {
        this.#clockOffsetMs = clockOffset(message.t, message.serverTime, Date.now());
        return;
      }
      if (message.kind === "hello" || message.kind === "resume") {
        // Seed the clock from the greeting so the first turn timer is already
        // correct, rather than waiting a full ping interval to find out.
        this.#clockOffsetMs = message.serverTime - Date.now();
      }
      this.handlers.onMessage(message);
      if (message.kind === "room" && message.room.lifecycle === "ABORTED") {
        this.#closed = true; this.#stopTimers(); ws.close();
      }
    });

    ws.addEventListener("close", () => {
      this.#stopTimers();
      if (this.#closed) return;
      this.handlers.onStatus?.("reconnecting");
      // Exponential backoff with jitter, so four clients dropped by the same
      // blip do not all come back in the same millisecond.
      const backoff = Math.min(MAX_BACKOFF, 400 * 2 ** this.#attempt);
      const delay = backoff * (0.7 + Math.random() * 0.6);
      this.#attempt += 1;
      this.#retryTimer = window.setTimeout(() => { void this.#retry(); }, delay);
    });

    ws.addEventListener("error", () => ws.close());
  }

  /**
   * Reconnect, unless the room has gone for good.
   *
   * After a couple of failures the room is asked over plain HTTP whether it
   * still exists, which does carry a status code. A 410 or 404 is terminal:
   * retrying cannot help, and the player needs to be told rather than left
   * watching a spinner.
   */
  async #retry(): Promise<void> {
    if (this.#closed) return;
    if (this.#attempt >= PROBE_AFTER_ATTEMPTS) {
      try {
        const response = await fetch(`/api/rooms/${this.roomId}`, { credentials: "same-origin" });
        if (response.status === 410 || response.status === 404) {
          this.#closed = true;
          this.handlers.onStatus?.("gone");
          return;
        }
      } catch {
        // The probe itself failed, which says nothing about the room. Retry.
      }
    }
    this.#open();
  }

  #ping(): void {
    this.send({ kind: "ping", t: Date.now() });
  }

  #stopTimers(): void {
    if (this.#pingTimer !== null) { window.clearInterval(this.#pingTimer); this.#pingTimer = null; }
    if (this.#retryTimer !== null) { window.clearTimeout(this.#retryTimer); this.#retryTimer = null; }
  }
}
